import { ChildModel, ModelBase } from 'store/model-base';

import { PlanSummary } from './plan-summary';
import { LineSummary } from './line-summary';
import { DataPlanType, PlanNames, UnlPlanSubgroup } from 'my-account/constants';
import { AccountType } from 'core/constants';
import { Util } from 'services/util';

export interface SummaryLineWithPlan {
    line: LineSummary;
    plan: PlanSummary;
}

export class AccountSummary extends ModelBase {
    public static storeName: string = 'accountSummary';
    public static colors: string[] = [ '#0C9BB1', '#7155d3', '#ff9271', '#ffd871', '#65d0a7', '#bc43d3', '#fb543f', '#ffa700', '#1ee2c9', '#0e8751' ];

    public cycleEndDate: string;
    public cycleDaysLeft: number;
    public planGroups: PlanSummary[];
    public hasSharedPlan: boolean;
    public bucketId: string;
    public sharedPlan: PlanSummary;
    public level: AccountType;
    public level2Customer: boolean;
    public level3Customer: boolean;
    public level4Customer: boolean;
    public level5Customer: boolean;
    public level6Customer: boolean;
    public isCbmNoLevel: boolean;

    public static create<T extends ModelBase>(response: ApiResponse = {}): T {
        response.sharedPlan = response.planGroups.find((plans: PlanSummary) => plans.planType === DataPlanType.BY_THE_GIG_SHARED);
        response.hasSharedPlan = Boolean(response.sharedPlan);
        response.bucketId = response.sharedPlan && response.sharedPlan.bucketId;
        response.level2Customer = response.level === AccountType.LEVEL_2;
        response.level3Customer = response.level === AccountType.LEVEL_3;
        response.level4Customer = response.level === AccountType.LEVEL_4;
        response.level5Customer = response.level === AccountType.LEVEL_5;
        response.level6Customer = response.level === AccountType.LEVEL_6;
        response.isCbmNoLevel = response.level === AccountType.NO_LEVEL;
        
        // assign colors to the icon leter
        let colorIndex: number = 0;
        const accountSummary: AccountSummary = super.create<AccountSummary>(response);

        for (const plan of accountSummary.planGroups) {
            for (const line of plan.lines) {
                const pairedLine: LineSummary = line.isActive && line.pairedLines.length && accountSummary.getLineByKey(line.pairedLines[0].lineId.key);
                // check if line count reach the color palette for color (If it reaches reset the count)
                colorIndex = colorIndex === AccountSummary.colors.length ? 0 : colorIndex;

                line.color = pairedLine && pairedLine.color ? pairedLine.color : AccountSummary.colors[colorIndex++];
            }
        }
        
        /* eslint-disable @typescript-eslint/no-explicit-any */
        return <T> <any> accountSummary;
        /* eslint-enable @typescript-eslint/no-explicit-any */
    }

    protected static get hasMany(): ChildModel[] {
        return [{
            attrName: 'planGroups',
            model: PlanSummary
        }];
    }

    /**
     * get Line By KEY
     * @param id id.key from Line Summary
     */

    public getLineSummaryWithPlanByKey(key: string): SummaryLineWithPlan {
        let toReturn: SummaryLineWithPlan;

        this.planGroups.some((plan: PlanSummary) =>
            plan.lines.some((line: LineSummary) => {
                if (line.id.key === key) {
                    toReturn = {
                        line,
                        plan
                    };

                    return true;
                }
            })
        );

        return toReturn;
    }

    public getLineByValue(value: string): LineSummary {
        for (const plan of this.planGroups) {
            for (const line of plan.lines) {
                if (line.id.value === value) {
                    return line;
                }
            }
        }
    }

    public getLineByKey(key: string): LineSummary {
        for (const plan of this.planGroups) {
            for (const line of plan.lines) {
                if (line.id.key === key) {
                    return line;
                }
            }
        }
    }

    public getLineSummaryByLineNumber(key: string): LineSummary {
        for (const plan of this.planGroups) {
            for (const line of plan.lines) {
                if (line.lineNumber === key) {
                    return line;
                }
            }
        }
    }

    public getBtgLineCountAndLineId(): { lineCount: number; lineId?: string } {
        const btgLines: LineSummary[] = [];
        
        this.planGroups.forEach((plan: PlanSummary) => {
            if (plan.planType === DataPlanType.BY_THE_GIG || plan.planType === DataPlanType.BY_THE_GIG_SHARED) {
                btgLines.push(...plan.lines);
            }
        });

        const lineCount: number = btgLines.length;
        
        return {
            lineCount,
            lineId: lineCount === 1 ? btgLines[0].id.key : undefined
        };
    }

    public get isNoTabletAccount(): boolean {
        return this.planGroups.every((plan: PlanSummary) => plan.isNoTabletPlan);
    }

    public get isTabletOnlyAccount(): boolean {
        return this.planGroups.every((plan: PlanSummary) => plan.isTabletOnlyPlan);
    }

    public get isTabletWithOtherLinesAccount(): boolean {
        let tabletFound: boolean;
        let otherDeviceFound: boolean;
        this.planGroups.forEach((plan: PlanSummary) => {
            plan.lines.forEach((line: LineSummary) => {
                line.isTablet ? tabletFound = true : otherDeviceFound = true;
            });
        });

        return tabletFound && otherDeviceFound;
    }

    public getBtgBucketId(): string {
        let bucketId: string;
        for (const plan of this.planGroups) {
            if (plan.planType === DataPlanType.BY_THE_GIG || plan.planType === DataPlanType.BY_THE_GIG_SHARED) {
                bucketId = plan.bucketId;
                break;
            }
        }

        return bucketId;
    }

    public get getLinesWithPlans(): LineSummary[] {
        const planTypes: string[] = [DataPlanType.BY_THE_GIG, DataPlanType.BY_THE_GIG_SHARED, DataPlanType.UNLIMITED, DataPlanType.TABLET, DataPlanType.SMART_WATCH];
        const sortedLines: LineSummary[] = [];

        for (const planType of planTypes) {
            const planGroup: PlanSummary = this.planGroups.find((plan: PlanSummary) => plan.planType === planType);
            if (planGroup) {
                planGroup.lines.filter((line: LineSummary) => line.isActive).forEach((line: LineSummary) => {
                    line.lineDataUsage = parseFloat(Util.mbToGb(line.dataUsage));
                    switch (planType) {
                        case DataPlanType.BY_THE_GIG:
                            line.planName = PlanNames.BY_THE_GIG;
                            break;
                        case DataPlanType.BY_THE_GIG_SHARED:
                            line.planName = PlanNames.BY_THE_GIG_SHARED;
                            break;
                        case DataPlanType.UNLIMITED:
                            if (line.planSubgroup === UnlPlanSubgroup.BASIC) {
                                line.planName = PlanNames.BASIC;
                            } else if (line.planSubgroup === UnlPlanSubgroup.PLUS) {
                                line.planName = PlanNames.PLUS;
                            } else if (line.planSubgroup === UnlPlanSubgroup.PREMIUM) {
                                line.planName = PlanNames.PREMIUM;
                            }
                            break;
                        case DataPlanType.TABLET:
                            if (line.planSubgroup === UnlPlanSubgroup.TABLET_PREMIUM) {
                                line.planName = PlanNames.TABLET_PREMIUM;
                            } else {
                                line.planName = PlanNames.TABLET;
                            }
                            break;
                        case DataPlanType.SMART_WATCH:
                            if (line.planSubgroup === UnlPlanSubgroup.STANDALONE) {
                                line.planName = PlanNames.STANDALONE_SMART_WATCH;
                            } else {
                                line.pairedLineNumber = this.getLineByKey(line.pairedLines[0]?.lineId?.key)?.lineNumber;
                            }
                            break;
                    }
                    sortedLines.push(line);
                });
            }
        }

        return sortedLines;
    }
}
